import React, { useState, useRef } from 'react';
import { Mic } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const VoiceDetectionDialog = ({ setShowVoiceDialog, onResult, navigateToTemplate = true }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const mediaRecorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const audioChunksRef = useRef([]);
  const navigate = useNavigate();

  const determineSection = (text) => {
    const lowerText = text.toLowerCase().trim();

    // Improved keyword mappings with MORE keywords and phrases for better accuracy
    const sectionMappings = {
      'зээлийн': {
        keywords: ['зээл', 'төлбөр', 'мөнгө', 'гэрээ', 'банк', 'төлөх', 'өр', 'зээлдэгч', 'зээлдүүлэгч'],
        phrases: ['зээлийн гэрээ', 'зээлийн нэхэмжлэл', 'мөнгө гаргуулах', 'төлбөр төлөх']
      },
      'ажилд': {
        keywords: ['ажилд', 'эргүүлэн', 'буцаах', 'тогтоолгох', 'халах', 'ажлаас', 'сэргээх', 'халсан', 'чөлөөлсөн'],
        phrases: ['ажилд эргүүлэн', 'ажилд буцаах', 'ажлаас халсан', 'ажилд эргүүлэн тогтоолгох', 'чөлөөлснөөс буцаах']
      },
      'ажилласанЖил': {
        keywords: ['ажилласан', 'жил', 'улс', 'үйлчилсэн', 'хугацаа', 'тогтоолгох', 'тоолгох', 'хөдөлмөр'],
        phrases: ['ажилласан жил', 'улсад ажилласан', 'жил тогтоолгох', 'үйлчилсэн жил', 'хөдөлмөрийн жил']
      },
      'эвлэрүүлэн': {
        keywords: ['эвлэрүүлэн', 'зуучлал', 'зуучлах', 'маргаан', 'өргөдөл', 'шийдвэрлэх', 'эвлэрэх'],
        phrases: ['эвлэрүүлэн зуучлал', 'зуучлалд өргөдөл', 'маргаан шийдвэрлэх']
      },
      'гэрлэлт': {
        keywords: ['гэрлэлт', 'салалт', 'гэр бүл', 'цуцлах', 'цуцлуулах', 'хүүхэд', 'асрамж', 'салах', 'гэрлэсэн'],
        phrases: ['гэрлэлт цуцлуулах', 'салалт', 'гэр бүлээс салах', 'хүүхдийн асрамж', 'гэрлэлт цуцлах']
      },
      'үүрэг': {
        keywords: ['үүрэг', 'гэрээ', 'хариуцлага', 'биелүүлэх', 'төлөх', 'гүйцэтгэх'],
        phrases: ['гэрээний үүрэг', 'үүрэг биелүүлэх', 'гэрээний хариуцлага']
      },
      'хамтын': {
        keywords: ['хамтын', 'амьдрал', 'хамт', 'амьдарсан', 'тогтоолгох', 'баталгаажуулах'],
        phrases: ['хамтын амьдрал', 'хамт амьдарсан', 'амьдралтай байсныг', 'хамтын амьдралтай']
      },
      'тэтгэлэг': {
        keywords: ['тэтгэлэг', 'тэтгэмж', 'хүүхэд', 'асрамж', 'эцэг', 'эх', 'гаргуулах', 'эцэг тогтоолгох'],
        phrases: ['хүүхдийн тэтгэлэг', 'тэтгэлэг гаргуулах', 'эцэг тогтоолгох', 'асрамжийн төлбөр', 'тэтгэмж авах']
      },
      'сураггүй': {
        keywords: ['сураггүй', 'алга', 'олдохгүй', 'тооцуулах', 'алга болсон', 'мэдээгүй'],
        phrases: ['сураггүй алга', 'алга болсонд', 'сураггүй алга болсонд', 'тооцуулах']
      },
      'эрхЗүй': {
        keywords: ['эрх зүй', 'чадамж', 'чадамжгүй', 'тооцуулах', 'асран хамгаалагч'],
        phrases: ['эрх зүйн чадамж', 'чадамжгүйд тооцуулах', 'эрх зүйн чадамжгүй']
      },
      'эдХөрөнгө': {
        keywords: ['эд хөрөнгө', 'гэм хор', 'хохирол', 'мөнгө', 'гаргуулах', 'нөхөн', 'төлбөр'],
        phrases: ['эд хөрөнгө гаргуулах', 'гэм хор учруулсан', 'хохирол нөхөх', 'эд хөрөнгийн хохирол']
      },
      'гүйцэтгэх': {
        keywords: ['гүйцэтгэх', 'хуудас', 'бичүүлэх', 'шийдвэр', 'дүгнэлт', 'биелүүлэх'],
        phrases: ['гүйцэтгэх хуудас', 'хуудас бичүүлэх', 'шийдвэр гүйцэтгэх']
      },
      'оронСууц': {
        keywords: ['орон сууц', 'өмчлөгч', 'тогтоолгох', 'эзэмшигч', 'байр', 'гэр'],
        phrases: ['орон сууцны өмчлөгч', 'өмчлөгчөөр тогтоолгох', 'байрны өмчлөгч']
      },
      'эрэнСурвалжлах': {
        keywords: ['эрэн сурвалжлах', 'эрэн', 'сурвалжлах', 'хайлт', 'олох'],
        phrases: ['эрэн сурвалжлах']
      },
      'насБарсан': {
        keywords: ['нас барсан', 'нас баралт', 'тооцуулах', 'өөд болох', 'таалал'],
        phrases: ['нас барсанд тооцуулах', 'нас барсанд', 'таалал төгссөн']
      },
      'улсынБүртгэл': {
        keywords: ['улсын бүртгэл', 'бүртгүүлэх', 'бүртгэл', 'улс', 'бүртгэх'],
        phrases: ['улсын бүртгэл', 'бүртгэлд бүртгүүлэх', 'улсын бүртгэлд']
      },
      'нөхөнОлговор': {
        keywords: ['нөхөн олговор', 'олговор', 'нөхөн', 'төлбөр', 'хохирол', 'гаргуулах'],
        phrases: ['нөхөн олговор', 'олговор гаргуулах', 'нөхөн төлбөр']
      }
    };

    let bestMatch = 'зээлийн';
    let maxScore = 0;

    Object.entries(sectionMappings).forEach(([section, { keywords, phrases }]) => {
      let score = 0;

      // Check for exact phrase matches (higher priority - 10 points)
      phrases.forEach(phrase => {
        if (lowerText.includes(phrase)) {
          score += 10;
        }
      });

      // Check for keyword matches (3 points each)
      keywords.forEach(keyword => {
        if (lowerText.includes(keyword)) {
          score += 3;
        }
      });

      if (score > maxScore) {
        maxScore = score;
        bestMatch = section;
      }
    });

    return bestMatch;
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          sampleSize: 16,
          echoCancellation: true,
          noiseSuppression: true,
        } 
      });

      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: 16000,
      });

      const mediaStreamSource = audioContextRef.current.createMediaStreamSource(stream);
      const recorder = audioContextRef.current.createScriptProcessor(4096, 1, 1);
      
      audioChunksRef.current = [];

      recorder.onaudioprocess = (e) => {
        const channelData = e.inputBuffer.getChannelData(0);
        audioChunksRef.current.push(new Float32Array(channelData));
      };

      mediaStreamSource.connect(recorder);
      recorder.connect(audioContextRef.current.destination);

      mediaRecorderRef.current = {
        stream,
        recorder,
        mediaStreamSource,
        stop: () => {
          recorder.disconnect();
          mediaStreamSource.disconnect();
          stream.getTracks().forEach(track => track.stop());
          
          const wavData = createWavFile(audioChunksRef.current, 16000);
          const audioBlob = new Blob([wavData], { type: 'audio/wav' });
          sendAudioToAPI(audioBlob);
        }
      };

      setTranscript('Сонсож байна...');
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recording:', error);
      setTranscript('Микрофон холбоход алдаа гарлаа');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setTranscript('Боловсруулж байна...');
    }
  };

  const createWavFile = (audioChunks, sampleRate) => {
    const numSamples = audioChunks.reduce((acc, chunk) => acc + chunk.length, 0);
    const sampleArray = new Float32Array(numSamples);
    let offset = 0;
    
    for (const chunk of audioChunks) {
      sampleArray.set(chunk, offset);
      offset += chunk.length;
    }

    const numChannels = 1;
    const bitsPerSample = 16;
    const bytesPerSample = bitsPerSample / 8;
    const blockAlign = numChannels * bytesPerSample;
    const byteRate = sampleRate * blockAlign;
    const dataSize = numSamples * bytesPerSample;
    const bufferLength = 44 + dataSize;
    const arrayBuffer = new ArrayBuffer(bufferLength);
    const view = new DataView(arrayBuffer);

    writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + dataSize, true);
    writeString(view, 8, 'WAVE');
    writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitsPerSample, true);
    writeString(view, 36, 'data');
    view.setUint32(40, dataSize, true);

    const volume = 0.8;
    let index = 44;
    for (let i = 0; i < sampleArray.length; i++) {
      const sample = Math.max(-1, Math.min(1, sampleArray[i])) * volume;
      const value = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
      view.setInt16(index, value, true);
      index += 2;
    }

    return new Uint8Array(arrayBuffer);
  };

  const writeString = (view, offset, string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };

  const sendAudioToAPI = async (audioBlob) => {
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.wav');

      console.log(audioBlob);

      const response = await fetch('https://kiosk-8nabk06g0-sergelens-projects.vercel.app/api/transcribe', {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      console.log(data);
      
      if (!response.ok) {
        throw new Error(data.error || data.detail || 'Server error');
      }

      if (data.transcription) {
        setTranscript(data.transcription);
        onResult(data.transcription);

        // Only navigate to document template if navigateToTemplate is true
        if (navigateToTemplate) {
          const selectedSection = determineSection(data.transcription);
          navigate("/documenttemplate", {
            state: {
              selectedOption: data.transcription,
              selectedSection: selectedSection,
            },
          });
        }
      } else {
        throw new Error('No transcription in response');
      }
      
      setTimeout(() => {
        setShowVoiceDialog(false);
      }, 1000);
    } catch (error) {
      console.error('Error sending audio to API:', error);
      setTranscript(`Алдаа гарлаа: ${error.message}`);
    }
  };

  const handleCancel = () => {
    if (mediaRecorderRef.current) {
      if (mediaRecorderRef.current.recorder) {
        mediaRecorderRef.current.recorder.disconnect();
      }
      if (mediaRecorderRef.current.mediaStreamSource) {
        mediaRecorderRef.current.mediaStreamSource.disconnect();
      }
      if (mediaRecorderRef.current.stream) {
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      }
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
    }
    setIsRecording(false);
    setShowVoiceDialog(false);
  };

  const handleOutsideClick = (e) => {
    if (e.target === e.currentTarget) {
      handleCancel();
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50" onClick={handleOutsideClick}>
      <div className="flex min-h-full items-center justify-center p-4 text-center">
        <div 
          className="w-full max-w-md transform overflow-hidden rounded-2xl p-6 text-left align-middle shadow-xl transition-all"
          style={{backgroundColor: '#21396c', color: 'white'}}
          onClick={e => e.stopPropagation()}
        >
          <div className="flex flex-col items-center">
            <button
              onMouseDown={startRecording}
              onMouseUp={stopRecording}
              onMouseLeave={stopRecording}
              onTouchStart={startRecording}
              onTouchEnd={stopRecording}
              className={`relative w-32 h-32 rounded-full transition-all duration-300 ${
                isRecording ? 'bg-red-500' : 'bg-blue-500'
              }`}
            >
              <div className={`absolute inset-0 rounded-full ${
                isRecording ? 'bg-red-500/20' : 'bg-blue-500/20'
              } animate-ping`} />
              <div className={`absolute inset-4 rounded-full ${
                isRecording ? 'bg-red-500/40' : 'bg-blue-500/40'
              } animate-ping animation-delay-150`} />
              <div className="absolute inset-0 w-32 h-32 flex items-center justify-center">
                <Mic size={48} className="text-white" />
              </div>
            </button>

            <div className="text-center mt-6 mb-4">
              <p className="text-gray-300 min-h-[1.5rem] text-lg">
                {isRecording ? 'Сонсож байна...' : transcript || 'Та дээрхи товч дээр удаан дарж үйлчилгээний нэр хэлж хайна уу!'}
              </p>
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 rounded-md bg-white text-[#21396c] hover:bg-gray-100 transition-colors duration-200 text-lg"
            >
              Болих
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoiceDetectionDialog;